import { forwardRef, type ComponentPropsWithoutRef } from "react"
import * as RadixTabs from "@radix-ui/react-tabs"
import * as RadixNavMenu from "@radix-ui/react-navigation-menu"
import { type Icon } from "./iconCore"

type TabsRootBase = {
  /**
   * Indicate whether to display this NavTabsRoot as a floating nav. By default
   * NavTabsRoot is intended to be displayed in the standard flow of the document
   * but the floating nav is intended to sit on top of content, typically in a
   * mobile context.
   */
  variant?: "default" | "floating"
}

type TabBaseProps = {
  /**
   * Icon to be displayed on the tab. If no activeIcon is displayed then this
   * icon will always be displayed and the fill will be updated according to
   * tab state.
   */
  icon?: Icon

  /**
   * Optionally provide an icon to be displayed when the tab is active
   * or hovered. The reason this option is provided is for cases of brand-specifc
   * icon colours that cannot be supplied by the NavTabLink component styles
   */
  activeIcon?: Icon
}

export type TabsRootProps = ComponentPropsWithoutRef<typeof RadixTabs.Root> &
  TabsRootBase

export const TabsRoot = forwardRef<HTMLDivElement, TabsRootProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <RadixTabs.Root
        ref={ref}
        className={`tabs-root ${className ?? ""}`}
        data-variant={variant}
        {...props}
      />
    )
  },
)

TabsRoot.displayName = "TabsRoot"

export type TabsListProps = ComponentPropsWithoutRef<typeof RadixTabs.List>

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.List
        ref={ref}
        className={`tabs-list ${className ?? ""}`}
        {...props}
      />
    )
  },
)

TabsList.displayName = "TabsList"

export type TabProps = ComponentPropsWithoutRef<typeof RadixTabs.Trigger> &
  TabBaseProps

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  (
    { className, children, icon: MainIcon, activeIcon: ActiveIcon, ...props },
    ref,
  ) => {
    return (
      <RadixTabs.Trigger
        ref={ref}
        className={`tab ${className ?? ""}`}
        {...props}
      >
        {/*
         * If both icons are present display them both.
         * Ultimately CSS will determine which one to show
         */}
        {ActiveIcon ? (
          <ActiveIcon className="tab-active-icon" data-size="md" />
        ) : null}
        {MainIcon ? (
          <MainIcon className="tab-main-icon" data-size="md" />
        ) : null}
        {children}
      </RadixTabs.Trigger>
    )
  },
)

Tab.displayName = "Tab"

export type TabContentProps = ComponentPropsWithoutRef<typeof RadixTabs.Content>

export const TabContent = forwardRef<HTMLDivElement, TabContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.Content
        ref={ref}
        className={`tab-content ${className ?? ""}`}
        {...props}
      />
    )
  },
)

TabContent.displayName = "TabContent"

export type NavTabsRootProps = ComponentPropsWithoutRef<
  typeof RadixNavMenu.Root
> &
  TabsRootBase

export const NavTabsRoot = forwardRef<HTMLElement, NavTabsRootProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <RadixNavMenu.Root
        ref={ref}
        className={`tabs-root ${className ?? ""}`}
        data-variant={variant}
        {...props}
      />
    )
  },
)

NavTabsRoot.displayName = "NavTabsRoot"

export type NavTabsListProps = ComponentPropsWithoutRef<
  typeof RadixNavMenu.List
>

export const NavTabsList = forwardRef<HTMLUListElement, NavTabsListProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixNavMenu.List
        ref={ref}
        className={`tabs-list ${className ?? ""}`}
        {...props}
      />
    )
  },
)

NavTabsList.displayName = "NavTabsList"

export type NavTabProps = ComponentPropsWithoutRef<typeof RadixNavMenu.Item>

export const NavTab = forwardRef<HTMLLIElement, NavTabProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixNavMenu.Item
        ref={ref}
        className={`nav-tab ${className ?? ""}`}
        {...props}
      />
    )
  },
)

NavTab.displayName = "NavTab"

export type NavTabLinkProps = ComponentPropsWithoutRef<
  typeof RadixNavMenu.Link
> &
  TabBaseProps

export const NavTabLink = forwardRef<HTMLAnchorElement, NavTabLinkProps>(
  (
    { className, children, icon: MainIcon, activeIcon: ActiveIcon, ...props },
    ref,
  ) => {
    return (
      <RadixNavMenu.Link
        ref={ref}
        className={`tab ${className ?? ""}`}
        {...props}
      >
        {/*
         * If both icons are present display them both.
         * Ultimately CSS will determine which one to show
         */}
        {ActiveIcon ? (
          <ActiveIcon className="tab-active-icon" data-size="md" />
        ) : null}
        {MainIcon ? (
          <MainIcon className="tab-main-icon" data-size="md" />
        ) : null}
        {children}
      </RadixNavMenu.Link>
    )
  },
)

NavTabLink.displayName = "NavTabLink"

export type NavTabLinkTextProps = ComponentPropsWithoutRef<"span"> & {
  /**
   * This is necessary to support a hack introduced to fix the layout
   * shift caused when the link text is bolded https://stackoverflow.com/a/32570813/3554402.
   * By inserting a hidden element that has the same content as the link and
   * by applying the bolded styles always we force the link to be the
   * size it would be if it were bolded. That way when the link becomes active,
   * showing bolded text does not cause a slight layout shift.
   *
   * Use the same text content that is in the <a /> tag.
   *
   * E.g.
   *
   * ```
   * <NavTabLinkText data-content="Chat">
   *  Chat
   * </NavTabLinkText>
   * ```
   */
  "data-content": string
}

export const NavTabLinkText = forwardRef<HTMLSpanElement, NavTabLinkTextProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`tab-link-text ${className ?? ""}`}
        {...props}
      />
    )
  },
)

NavTabLinkText.displayName = "NavTabLinkText"

export type TextTabsRootProps = ComponentPropsWithoutRef<typeof RadixTabs.Root>

export const TextTabsRoot = forwardRef<HTMLDivElement, TextTabsRootProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.Root
        ref={ref}
        className={`text-tabs-root ${className ?? ""}`}
        {...props}
      />
    )
  },
)

TextTabsRoot.displayName = "TextTabsRoot"

export type TextTabsListProps = ComponentPropsWithoutRef<typeof RadixTabs.List>

export const TextTabsList = forwardRef<HTMLDivElement, TextTabsListProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.List
        ref={ref}
        className={`text-tabs-list ${className ?? ""}`}
        {...props}
      />
    )
  },
)

TextTabsList.displayName = "TextTabsList"

export type TextTabProps = ComponentPropsWithoutRef<typeof RadixTabs.Trigger>

export const TextTab = forwardRef<HTMLButtonElement, TextTabProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.Trigger
        ref={ref}
        className={`text-tab ${className ?? ""}`}
        {...props}
      />
    )
  },
)

TextTab.displayName = "TextTab"

export type HeaderTabsRootProps = ComponentPropsWithoutRef<
  typeof RadixTabs.Root
>

export const HeaderTabsRoot = forwardRef<HTMLDivElement, HeaderTabsRootProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.Root
        ref={ref}
        className={`header-tabs-root ${className ?? ""}`}
        {...props}
      />
    )
  },
)

HeaderTabsRoot.displayName = "HeaderTabsRoot"

export type HeaderTabsListProps = ComponentPropsWithoutRef<
  typeof RadixTabs.List
>

export const HeaderTabsList = forwardRef<HTMLDivElement, HeaderTabsListProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.List
        ref={ref}
        className={`header-tabs-list ${className ?? ""}`}
        {...props}
      />
    )
  },
)

HeaderTabsList.displayName = "HeaderTabsList"

export type HeaderTabProps = ComponentPropsWithoutRef<typeof RadixTabs.Trigger>

export const HeaderTab = forwardRef<HTMLButtonElement, HeaderTabProps>(
  ({ className, ...props }, ref) => {
    return (
      <RadixTabs.Trigger
        ref={ref}
        className={`header-tab ${className ?? ""}`}
        {...props}
      />
    )
  },
)

HeaderTab.displayName = "HeaderTab"
